import React, { useState } from "react";
import { BRANDING } from "../../config/branding";

export default function TopBar({
  postcode,
  defaultPostcode,
  onPostcodeChange,
  onChangePostcode,
  onPostcodeSubmit,
  onSubmitPostcode,
  error,
  /** Phone layout: toggles slide-over school list */
  onToggleSchoolList,
  schoolListOpen = false,
  /** Mobile filter button */
  onToggleFilters,
  hasActiveFilters = false,
  onOpenMap,
  onOpenRanking,
  onOpenCompare,
  onOpenHome,
  onOpenAccount,
  rankingOpen = false,
  compareOpen = false,
  homeOpen = false,
  accountOpen = false,
}) {
  const handleChange = onPostcodeChange ?? onChangePostcode ?? (() => {});
  const handleSubmit = onPostcodeSubmit ?? onSubmitPostcode ?? (() => {});

  // Local draft so click-to-clear does not rewrite shared home/postcode (avoids ranking shake).
  const [draft, setDraft] = useState(null);
  const editing = draft !== null;
  const shown = editing ? draft : postcode;
  const isDefault = !editing && defaultPostcode && postcode === defaultPostcode;

  const commitShown = (raw) => {
    const next = String(raw ?? "").trim();
    if (!next) {
      handleChange(defaultPostcode ?? "");
      setDraft(null);
      return defaultPostcode ?? "";
    }
    handleChange(next);
    setDraft(null);
    return next;
  };

  return (
    <div className="v2-topbar">
      <button type="button" className="v2-brand v2-brand-home" onClick={onOpenMap}>
        <img className="v2-logo" src={BRANDING.logoSrc} alt="" />
        <div className="v2-brand-text">
          <div className="v2-appname">{BRANDING.appName}</div>
          {BRANDING.tagline ? (
            <div className="v2-tagline">{BRANDING.tagline}</div>
          ) : null}
        </div>
      </button>

      <div className="v2-topbar-sample">
      <div className="v2-topbar-left">
        <form
          className="v2-topbar-postcode-form"
          onSubmit={(e) => {
            e.preventDefault();
            const next = commitShown(shown);
            handleSubmit?.(next);
          }}
        >
          <div className="v2-postcode-field">
            <input
              className={`v2-postcode ${isDefault ? "v2-postcode--default" : ""}`}
              value={shown}
              placeholder="e.g. SW19 5AB"
              enterKeyHint="go"
              onChange={(e) => setDraft(e.target.value)}
              onFocus={() => {
                // Clear the field on click/focus; keep parent postcode until blur/submit.
                setDraft("");
              }}
              onBlur={() => {
                if (!editing) return;
                if (!String(draft || "").trim()) {
                  setDraft(null);
                  return;
                }
                commitShown(draft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const next = commitShown(shown);
                  handleSubmit?.(next);
                }
              }}
              style={{
                backgroundImage: 'url("/icons/home_Board_opaque.png")',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "14px center",
                backgroundSize: "16px 16px",
                paddingLeft: 40,
              }}
            />
            <button type="submit" className="v2-postcode-go" aria-label="Apply postcode" title="Apply postcode">
              <span className="v2-postcode-go-label">Go</span>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </button>
          </div>
        </form>

        {error ? (
          <div className="v2-topbar-error" style={{ color: "#b91c1c", fontSize: 12, marginLeft: 8 }}>
            {error}
          </div>
        ) : null}
      </div>

      {onToggleFilters ? (
        <button
          type="button"
          className={`v2-topbar-filter-btn${hasActiveFilters ? " v2-topbar-filter-btn--active" : ""}`}
          onClick={onToggleFilters}
          aria-label="Filter schools"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
        </button>
      ) : null}

      {onToggleSchoolList ? (
        <button
          type="button"
          className={`v2-topbar-schools-btn${schoolListOpen ? " v2-topbar-schools-btn--open" : ""}`}
          onClick={() => onToggleSchoolList()}
          aria-expanded={schoolListOpen}
          aria-controls="v2-school-list-panel"
          id="v2-topbar-schools-trigger"
        >
          Schools
        </button>
      ) : null}

      <nav className="v2-nav" aria-label="Pages">
        <button
          type="button"
          className={`v2-nav-link${homeOpen ? " is-on" : ""}`}
          aria-current={homeOpen ? "page" : undefined}
          onClick={onOpenHome}
        >
          Home
        </button>
        <button
          type="button"
          className={`v2-nav-link${!rankingOpen && !compareOpen && !homeOpen && !accountOpen ? " is-on" : ""}`}
          aria-current={!rankingOpen && !compareOpen && !homeOpen && !accountOpen ? "page" : undefined}
          onClick={onOpenMap}
        >
          Map
        </button>
        <button
          type="button"
          className={`v2-nav-link${rankingOpen ? " is-on" : ""}`}
          aria-current={rankingOpen ? "page" : undefined}
          onClick={onOpenRanking}
        >
          Ranking
        </button>
        <button
          type="button"
          className={`v2-nav-link${compareOpen ? " is-on" : ""}`}
          aria-current={compareOpen ? "page" : undefined}
          onClick={onOpenCompare}
        >
          Compare
        </button>
        <button
          type="button"
          className={`v2-nav-link${accountOpen ? " is-on" : ""}`}
          aria-current={accountOpen ? "page" : undefined}
          onClick={onOpenAccount}
        >
          Account
        </button>
      </nav>
      </div>
    </div>
  );
}
